import React, { forwardRef, HTMLProps, useRef, useState } from 'react'
import { usePopper } from 'react-popper'
import { createPortal } from 'react-dom'
import { useClickOutsideRef } from './hooks/useClickOutsideRef'
import InfoIcon from './logos/info'
import CloseIcon from "./logos/close";

interface TooltipProps {
    id: string
    label?: string
}

export const Tooltip: React.FC<React.PropsWithChildren<TooltipProps>> = ({
    children,
    id,
    label
}) => {
    const [isOpen, setOpen] = useState(false)
    const buttonRef = useRef(null)
    const tooltipRef = useRef(null)
    const [arrowRef, setArrowRef] = useState(null)
    // const trackPulseEvent = useTrackEvent();

    const { styles, attributes } = usePopper(
        buttonRef.current,
        tooltipRef.current,
        {
            placement: 'bottom',
            modifiers: [
                { name: 'arrow', options: { element: arrowRef } },
                { name: 'offset', options: { offset: [0, 20] } }
            ]
        }
    )

    useClickOutsideRef([tooltipRef, buttonRef], () => setOpen(false))

    return (
        <span
            style={{ width: '29px', height: '15px', top: '-16px' }}
            className="relative inline-block ml-auto"
        >
            <InfoButton
                aria-label={
                    label ? `mer informasjon om ${label}` : 'mer informasjon'
                }
                isPopupOpen={isOpen}
                ref={buttonRef}
                onClick={(event) => {
                    // trackPulseEvent({ type: 'HELP_ICON_CLICK', helpText: `help-${id}` });
                    setOpen(!isOpen)
                    event.preventDefault()
                    event.stopPropagation()
                }}
            >
                <InfoIcon />
            </InfoButton>
            <div className="sr-only" role="status">
                {isOpen && children}
            </div>
            {isOpen &&
                createPortal(
                    <TooltipBubble
                        ref={tooltipRef}
                        style={styles.popper}
                        {...attributes.popper}
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                        }}
                    >
                        <span aria-hidden>
                            {
                                <div>
                                    <button
                                        type="button"
                                        style={{
                                            height: '34px',
                                            width: '34px',
                                            left: '1px',
                                            top: '0',
                                            backgroundColor: 'white',
                                            padding: '4.5px'
                                        }}
                                        onClick={() => setOpen(false)}
                                        className={`focus:border-aqua-300 hover:border-aqua-300 absolute inline cursor-pointer rounded-full border border-transparent text-center align-top text-gray-500 hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus:outline-none ${
                                            true ? 'border-aqua-300 border' : ''
                                        }`}
                                    >
                                        <CloseIcon></CloseIcon>
                                    </button>
                                    {children}
                                </div>
                            }
                        </span>
                        <Arrow
                            data-popper-arrow
                            ref={setArrowRef as any}
                            style={styles.arrow}
                            id="arrow"
                        />
                    </TooltipBubble>,
                    document.body
                )}
        </span>
    )
}

type InfoButtonProps = {
    isPopupOpen: boolean
}

const InfoButton = forwardRef(
    (
        {
            isPopupOpen,
            ...rest
        }: InfoButtonProps & HTMLProps<HTMLButtonElement>,
        ref
    ) => (
        <button
            ref={ref as any}
            {...rest}
            type="button"
            style={{
                height: '34px',
                width: '34px',
                left: '1px',
                top: '-5.5px',
                backgroundColor: 'white',
                padding: '4.5px'
            }}
            className={`focus:border-aqua-300 hover:border-aqua-300 absolute inline cursor-pointer rounded-full border border-transparent text-center align-top text-gray-500 hover:bg-gray-200 focus:bg-gray-200 focus:outline-none focus:outline-none ${
                isPopupOpen ? 'border-aqua-300 border' : ''
            }`}
        >
            {rest.children}
        </button>
    )
)


const TooltipBubble = forwardRef<any, HTMLProps<HTMLDivElement>>(
    (props, ref) => (
        <div {...props} className="tooltip-bubble" ref={ref}>
            {props.children}
        </div>
    )
)

const Arrow = forwardRef<any, HTMLProps<HTMLDivElement>>((props, ref) => (
    <div {...props} className="arrow text-white" ref={ref}/>
))
